/**
 * Smart Prefetch - Préchargement intelligent pour CareWashGo
 * Navigation prédictive et préchargement de ressources
 * 
 * NOTE: Désactivé sur les apps natives Android pour éviter les problèmes de performance
 */

import { Capacitor } from '@capacitor/core';

const IS_NATIVE = Capacitor.isNativePlatform();
const IS_ANDROID = Capacitor.getPlatform() === 'android';

// Configuration
const config = {
  prefetchDelay: 100,      // Délai avant prefetch sur hover
  intersectionThreshold: 0.1,
  maxPrefetchedPages: 5,
  connectionTypes: ['4g', 'wifi'] // Ne prefetch que sur bonne connexion
};

// Cache des pages préchargées
const prefetchedPages = new Set();
const prefetchQueue = [];
let isPrefetching = false;

/**
 * Vérifier si on peut prefetch (connexion, data saver, etc.)
 */
function canPrefetch() {
  // DÉSACTIVER sur Android natif pour éviter les problèmes
  if (IS_ANDROID) {
    return false;
  }
  
  // Vérifier que le DOM est disponible
  if (typeof document === 'undefined' || !document.head) {
    return false;
  }
  
  // Vérifier Save-Data header
  if (navigator.connection?.saveData) {
    return false;
  }
  
  // Vérifier le type de connexion
  const effectiveType = navigator.connection?.effectiveType;
  if (effectiveType && !config.connectionTypes.includes(effectiveType)) {
    return false;
  }
  
  // Vérifier si trop de pages préchargées
  if (prefetchedPages.size >= config.maxPrefetchedPages) {
    return false;
  }
  
  return true;
}

/**
 * Prefetch une page
 */
export function prefetchPage(url) {
  if (!canPrefetch()) return;
  if (prefetchedPages.has(url)) return;
  
  try {
    // Créer un link prefetch
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    
    document.head.appendChild(link);
    prefetchedPages.add(url);
    
  } catch (e) {
    console.warn('[SmartPrefetch] Error:', e);
  }
}

/**
 * Preload une ressource critique
 */
export function preloadResource(url, type = 'script') {
  if (IS_ANDROID) return; // Désactiver sur Android
  if (typeof document === 'undefined' || !document.head) return;
  
  try {
    const existing = document.querySelector(`link[href="${url}"]`);
    if (existing) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = type;
    
    if (type === 'font') {
      link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  } catch (e) {
    console.warn('[SmartPrefetch] Preload error:', e);
  }
}

/**
 * Prefetch sur hover d'un lien
 */
export function setupHoverPrefetch() {
  let hoverTimeout = null;
  
  document.addEventListener('mouseover', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    
    const href = link.getAttribute('href');
    
    // Ignorer les liens externes et ancres
    if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) {
      return;
    }
    
    // Délai avant prefetch
    hoverTimeout = setTimeout(() => {
      prefetchPage(href);
    }, config.prefetchDelay);
  });
  
  document.addEventListener('mouseout', (e) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
  });
  
}

/**
 * Prefetch basé sur la visibilité (Intersection Observer)
 */
export function setupVisibilityPrefetch() {
  if (!('IntersectionObserver' in window)) return;
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const link = entry.target;
        const href = link.getAttribute('href');
        
        if (href && !href.startsWith('http') && !href.startsWith('#')) {
          prefetchPage(href);
        }
        
        observer.unobserve(link);
      }
    });
  }, {
    threshold: config.intersectionThreshold,
    rootMargin: '50px'
  });
  
  // Observer tous les liens internes
  document.querySelectorAll('a[href^="/"]').forEach(link => {
    observer.observe(link);
  });
  
}

/**
 * Prefetch prédictif basé sur les patterns de navigation
 */
export function setupPredictivePrefetch() {
  // Patterns de navigation courants
  const navigationPatterns = {
    '/': ['/leaderboard', '/badges', '/profile'],
    '/login': ['/'],
    '/leaderboard': ['/', '/profile'],
    '/profile': ['/', '/badges', '/stats'],
    '/badges': ['/', '/trophies'],
    '/games': ['/spin-wheel', '/puzzle', '/quick-tap'],
    '/add-wash': ['/'],
  };
  
  const currentPath = window.location.pathname;
  const predictedPages = navigationPatterns[currentPath] || [];
  
  // Utiliser requestIdleCallback pour ne pas bloquer
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      predictedPages.forEach(page => prefetchPage(page));
    }, { timeout: 2000 });
  } else {
    setTimeout(() => {
      predictedPages.forEach(page => prefetchPage(page));
    }, 1000);
  }
}

/**
 * Précharger les images visibles dans le viewport
 */
export function setupImageLazyLoad() {
  if (!('IntersectionObserver' in window)) return;
  
  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        const src = img.dataset.src;
        
        if (src) {
          img.src = src;
          img.removeAttribute('data-src');
        }
        
        imageObserver.unobserve(img);
      }
    });
  }, {
    rootMargin: '100px 0px',
    threshold: 0.01
  });
  
  // Observer toutes les images avec data-src
  document.querySelectorAll('img[data-src]').forEach(img => {
    imageObserver.observe(img);
  });
  
  return imageObserver;
}

/**
 * Précharger les chunks de code
 */
export function preloadChunks(chunkNames) {
  chunkNames.forEach(name => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = `/static/js/${name}.chunk.js`;
    document.head.appendChild(link);
  });
}

/**
 * DNS Prefetch pour les domaines externes
 */
export function setupDNSPrefetch(domains) {
  domains.forEach(domain => {
    const existing = document.querySelector(`link[href="${domain}"]`);
    if (existing) return;
    
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    document.head.appendChild(link);
  });
}

/**
 * Preconnect aux domaines critiques
 */
export function setupPreconnect(domains) {
  domains.forEach(domain => {
    const existing = document.querySelector(`link[rel="preconnect"][href="${domain}"]`);
    if (existing) return;
    
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Initialiser le smart prefetch
 */
export function initSmartPrefetch() {
  if (typeof window === 'undefined') return;
  
  // Attendre que la page soit idle
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      setupHoverPrefetch();
      setupPredictivePrefetch();
      
      // DNS prefetch pour services externes
      setupDNSPrefetch([
        'https://api.dicebear.com',
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
      ]);
      
    }, { timeout: 3000 });
  } else {
    setTimeout(() => {
      setupHoverPrefetch();
      setupPredictivePrefetch();
    }, 2000);
  }
}

export default {
  prefetchPage,
  preloadResource,
  setupHoverPrefetch,
  setupVisibilityPrefetch,
  setupPredictivePrefetch,
  setupImageLazyLoad,
  preloadChunks,
  setupDNSPrefetch,
  setupPreconnect,
  initSmartPrefetch
};
